import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const apiBase = process.env.REACT_APP_API_URL || '/api';

// Défini hors du hook : objet stable, pas re-créé à chaque render
const DEFAULT_TYPE_COLORS = {
    'Rappel mensuel': '#3b82f6',
    'Rappel hebdomadaire': '#8b5cf6',
    'Rappel quotidien': '#06b6d4',
    'Notification': '#f59e0b',
    'Information': '#10b981',
    'Alerte': '#ef4444',
    'Mise à jour': '#6366f1'
};

function normalizeTypes(rawTypes) {
    return (Array.isArray(rawTypes) ? rawTypes : [])
        .map(t => {
            if (!t) return null;
            if (typeof t === 'string') return { name: t, color: DEFAULT_TYPE_COLORS[t] || '#000000' };
            if (typeof t === 'object') return { name: t.name, color: t.color || DEFAULT_TYPE_COLORS[t.name] || '#000000' };
            return null;
        })
        .filter(Boolean);
}

export default function useMessageSettings() {
    const { getAuthHeaders, activeTenantId } = useAuth();
    const [messageTypes, setMessageTypes] = useState([]);
    const [messageRoles, setMessageRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchSettings = useCallback(async (signal) => {
        if (!activeTenantId) {
            setMessageTypes([]);
            setMessageRoles([]);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const headers = getAuthHeaders ? getAuthHeaders() : {};
            const res = await fetch(
                `${apiBase}/tenants/${activeTenantId}/settings/messages`,
                { headers, ...(signal && { signal }) }
            );

            if (signal?.aborted) return;

            const contentType = res.headers.get('content-type') || '';
            const data = contentType.includes('application/json') ? await res.json() : null;

            if (!res.ok) {
                throw new Error(data?.message || `Failed to load message settings (${res.status})`);
            }

            setMessageTypes(normalizeTypes(data?.messageTypes));
            setMessageRoles(Array.isArray(data?.messageRoles) ? data.messageRoles : []);
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError(err);
            setMessageTypes([]);
            setMessageRoles([]);
        } finally {
            setLoading(false);
        }
    }, [activeTenantId, getAuthHeaders]);

    useEffect(() => {
        const controller = new AbortController();
        fetchSettings(controller.signal);
        return () => controller.abort();
    }, [fetchSettings]);

    // Écouter les mises à jour globales depuis d'autres composants
    useEffect(() => {
        const handler = () => fetchSettings();
        window.addEventListener('messageSettingsChanged', handler);
        return () => window.removeEventListener('messageSettingsChanged', handler);
    }, [fetchSettings]);

    const refresh = useCallback(() => fetchSettings(), [fetchSettings]);

    return { messageTypes, messageRoles, loading, error, refresh };
}
